import { Injectable } from '@angular/core';
import { ConciergeSettingsData } from 'src/lib/fliko/models/concierge/concierge.data';
import { CompReportType } from 'src/lib/fliko/models/company/companyLog.data';
import { CompanyDefectSettings, ReviewSettings } from 'src/lib/fliko/models/company/company.data';
import { DefaultRoles, EmployeePermissions, EmployeeRole } from 'src/lib/fliko/models/company/employeeRole.data';
import { SsoWhitelistData } from 'src/lib/fliko/models/company/sso.data';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { EstateInsuranceData } from 'src/lib/fliko/models/estate/insurance.data';
import { EstateDetailsData } from 'src/lib/fliko/models/estate/estate-detail.data';
import { SMSStatistics } from 'src/lib/fliko/models/estate/estate-statistics.data';
import { ReviewData, ReviewReportData } from 'src/lib/fliko/models/estate/review.data';
import { EstateSubcontractorData, SubcontractorData } from 'src/lib/fliko/models/estate/subcontractor.data';
import { CompanyConfigData, CompanyConfigSetRequest } from 'src/lib/fliko/models/http/companyConfig.data';
import { ResetEmployeePasswordReq, ResetPasswordReq } from 'src/lib/fliko/models/http/request.data';
import {
  AcceptAccessCtx,
  ProvideAccessCtx,
  ProvideWLAccessCtx,
  RequestAccessCtx,
  SsoUserCtx,
  WhitelistCtx
} from 'src/lib/fliko/models/http/ssoRequest.data';
import { SMSBlacklistData } from 'src/lib/fliko/models/integrations/blacklist.data';
import { SendSMSData } from 'src/lib/fliko/models/integrations/sendSMS.data';
import { ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { EstateAdminData, ServiceUserData, UserType } from 'src/lib/fliko/models/users/user.data';
import { CreatedUser, ActivationContext, CreateUserContext } from 'src/lib/fliko/models/users/createdUser.data';
import { FormTemplateData, FormTemplateType } from 'src/lib/fliko/models/form-template.data';
import {
  CompanyDashboardData,
  CompanyDashboardRequest,
  CompanyDashboardSettingsData,
  CompanyDashboardSettingsSaveRequest
} from 'src/lib/fliko/models/company/company-dashboard.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { FunctionsApiService, Result } from 'src/app/shared/services/functions-api.service';

/**
 *  Company Service that gets company data from Api
 */
@Injectable({ providedIn: 'root' })
export class CompanyApiService {
  private onReject = (reason: any) => {
    if (reason.code === 'functions/permission-denied') {
      this.errorService.showGlobalError(this.errorService.getErrorText('permDenied') ?? reason.message);
    }
  };

  constructor(
    private fas: FunctionsApiService,
    private errorService: ErrorHandlingService
  ) {}

  public async getCompanyConciergeSettings(companyId: string, estateId: string): Result<ConciergeSettingsData | null> {
    return this.fas.callFunction<ConciergeSettingsData | null>(
      'getCompanyConciergeSettings',
      { companyId, estateId },
      this.onReject
    );
  }

  public async getCompanyDefectSettings(companyId: string, estateId: string): Result<CompanyDefectSettings | null> {
    return this.fas.callFunction<CompanyDefectSettings | null>(
      'getCompanyDefectSettings',
      { companyId, estateId },
      this.onReject
    );
  }

  public async getCompanyReviewSettings(companyId: string): Result<ReviewSettings | null> {
    return this.fas.callFunction<ReviewSettings | null>('getCompanyReviewSettings', { companyId }, this.onReject);
  }

  public async getCompanyTemplates(
    companyId: string,
    estateId: string,
    type: FormTemplateType
  ): Result<FormTemplateData<string>[]> {
    return this.fas.callFunction<FormTemplateData<string>[]>('getCompanyTemplates', { companyId, estateId, type }, this.onReject);
  }

  /** @return {Result<EstateData[]>} all VISIBLE estates in company */
  public getCompanyEstates(companyId: string): Result<EstateData[]> {
    return this.dataFromApi('companyEstates', { companyId });
  }

  public getCompanyEstateDetails(companyId: string): Result<EstateDetailsData[]> {
    return this.dataFromApi('companyEstateDetails', { companyId });
  }

  public getCompanyConfig(companyId: string): Result<CompanyConfigData> {
    return this.dataFromApi('getCompanyConfig', { companyId });
  }

  public setCompanyEstateConfig(request: CompanyConfigSetRequest): Result<void> {
    return this.dataFromApi('setCompanyEstateConfig', request);
  }

  public getCompanyEstateAdmins(companyId: string): Result<EstateAdminData[]> {
    return this.dataFromApi('companyEstateAdmins', { companyId });
  }

  public getCompanyEstateInsurances(companyId: string): Result<{ string: EstateInsuranceData }[]> {
    return this.dataFromApi('companyEstateInsurances', { companyId });
  }

  public getCompanySubcontractors(companyId: string): Result<SubcontractorData[]> {
    return this.dataFromApi('companySubcontractors', { companyId });
  }

  public getCompanyEstateSubcontractors(companyId: string): Result<EstateSubcontractorData[]> {
    return this.dataFromApi('companyEstateSubcontractors', { companyId });
  }

  public getServiceUsers(companyId: string): Result<ServiceUserData[]> {
    return this.dataFromApi('companyServiceUsers', { companyId });
  }

  public getCompanyReviews(companyId: string): Result<ReviewData[]> {
    return this.dataFromApi('companyReviews', { companyId });
  }

  public getCompanyReviewReports(companyId: string): Result<ReviewReportData[]> {
    return this.dataFromApi('companyReviewReports', { companyId });
  }

  public getDefaultRoles(companyId: string): Result<DefaultRoles> {
    return this.dataFromApi('getDefaultRoles', { companyId });
  }

  public setEmployeeRole(companyId: string, userId: string, role: EmployeeRole): Result<boolean> {
    return this.employeeEdit('setEmployeeRole', companyId, userId, { role });
  }

  public updateEmployeePermissions(companyId: string, userId: string, perm: EmployeePermissions): Result<boolean> {
    return this.employeeEdit('updateEmployeePermissions', companyId, userId, { perm });
  }

  public getCompanySentSms(companyId: string): Result<SendSMSData[]> {
    return this.dataFromApi('companySentSms', { companyId });
  }

  public getCompanySmsBlacklist(companyId: string): Result<SMSBlacklistData[]> {
    return this.dataFromApi('companySmsBlacklist', { companyId });
  }

  public getCompanyResidents(companyId: string): Result<ResidentData[]> {
    return this.dataFromApi('companyResidents', { companyId });
  }

  public getCompanyDashboardData(request: CompanyDashboardRequest): Result<CompanyDashboardData> {
    return this.dataFromApi('companyDashboardData', request);
  }

  public getCompanyDashboardSettings(companyId: string): Result<CompanyDashboardSettingsData | null> {
    return this.dataFromApi('companyDashboardSettings', { companyId });
  }

  public setCompanyDashboardSettings(request: CompanyDashboardSettingsSaveRequest): Result<boolean> {
    return this.dataFromApi('setCompanyDashboardSettings', request);
  }

  public getCompanySmsStats(companyId: string): Result<({ id: string } & SMSStatistics)[]> {
    return this.dataFromApi('companySmsStats', { companyId });
  }

  public addEstateAccess(companyId: string, userId: string, estates: string[]): Result<boolean> {
    return this.employeeEdit('addEstateAccess', companyId, userId, { estates });
  }

  /** For test reasons */
  public sauterTest(): Result<boolean> {
    const yearZero = new Date('0001-01-01T00:00:00Z').valueOf();
    const date1 = new Date('2026-01-12T00:00:00.000Z').valueOf() - yearZero;
    const date2 = new Date('2026-01-13T00:00:00.000Z').valueOf() - yearZero;
    return this.dataFromApi('callSauterAPI', {
      path: 'HistoricalData',
      params: {
        pageNumber: 1,
        itemsPerPage: 100,
        objectId: 119,
        dateFrom: (date1 * 10000).toString(),
        dateTo: (date2 * 10000).toString()
      }
    });
  }

  /** For test reasons */
  public rogerTest(): Result<void> {
    return this.dataFromApi('rogerTestApi', {});
  }

  public removeEstateAccess(companyId: string, userId: string, estateId: string): Result<boolean> {
    return this.employeeEdit('removeEstateAccess', companyId, userId, { estateId });
  }

  public createUser(user: CreatedUser): Result<any> {
    return this.fas.callFunction<CreateUserContext>('createUser', { user }, this.onReject);
  }

  public sendMobileActivationLink(ctx: ActivationContext): Result<boolean> {
    return this.fas.callFunctionOrThrow<ActivationContext, boolean>('sendMobileActivationLink', ctx);
  }

  public generateReport(companyId: string, type: CompReportType): Result<boolean> {
    return this.fas.callFunction<boolean>('generateReport', { companyId, type }, this.onReject);
  }

  public toggleBlock(companyId: string, userId: string, state: UserType): Result<boolean> {
    return this.employeeEdit('toggleBlock', companyId, userId, { state });
  }

  public resetPassword(email: string): Promise<void> {
    return this.requestPasswordReset({ email });
  }

  public resetEmployeePassword(employee: ResetEmployeePasswordReq): Promise<void> {
    return this.requestPasswordReset({ employee });
  }

  private dataFromApi<T, R>(funcName: string, data: R): Result<T> {
    return this.fas.callFunction<T>(funcName, data, this.onReject);
  }

  private requestPasswordReset(request: ResetPasswordReq): Promise<void> {
    return this.fas.callFunctionOrThrow<ResetPasswordReq, void>('resetPassword', request);
  }

  private employeeEdit(funcName: string, companyId: string, userId: string, params?: any): Result<boolean> {
    return this.fas.callFunction<boolean>(funcName, { companyId, userId, ...params }, this.onReject);
  }

  // #region SSO User Management

  public acceptSsoUser(data: AcceptAccessCtx): Promise<boolean> {
    return this.fas.callFunction<boolean>('acceptSsoUser', data, this.onReject);
  }

  public rejectSsoUser(data: ProvideAccessCtx): Promise<boolean> {
    return this.fas.callFunction<boolean>('rejectSsoUser', data, this.onReject);
  }

  public checkWhitelistAccess(data: ProvideWLAccessCtx): Promise<boolean> {
    return this.fas.callFunction<boolean>('checkWhitelistAccess', data, this.onReject);
  }

  public requestSsoAccess(data: RequestAccessCtx): Promise<boolean> {
    return this.fas.callFunction<boolean>('requestSsoAccess', data, this.onReject);
  }

  public addToWhitelist(data: WhitelistCtx): Promise<SsoWhitelistData | null> {
    return this.fas.callFunction<SsoWhitelistData | null>('addToWhitelist', data, this.onReject);
  }

  public removeFromWhitelist(data: SsoUserCtx): Promise<SsoWhitelistData | null> {
    return this.fas.callFunction<SsoWhitelistData | null>('removeFromWhitelist', data, this.onReject);
  }

  public updateWhitelist(data: WhitelistCtx): Promise<SsoWhitelistData | null> {
    return this.fas.callFunction<SsoWhitelistData | null>('updateWhitelist', data, this.onReject);
  }

  // #endregion SSO User Management
}
